import { reactive, shallowRef } from 'vue'
import type { PortCallTypeRef } from '@gat-ship/types'
import type {
  AddPortCallTypePayload,
  PortCallTypesService,
  UpdatePortCallTypePayload
} from '@gat-ship/types/injection-keys'
import {
  removePortCallTypeById,
  sortPortCallTypesByName,
  upsertPortCallType
} from '../model/portcall-type-collection'
import { getPortCallTypesRequest } from './portcall-type-api-calls'

// Module-singleton state, ref-data style (see the datasources in
// `@gat-ship/ref-data`): every datasource instance shares one registry, so a
// mutation made in the administration modal is immediately visible to every
// other consumer. The registry is tenant-level reference data — there is no
// per-tree variant of it.
const portCallTypes = shallowRef<PortCallTypeRef[]>([])
const isLoading = reactive({ portCallTypes: false })

let inFlightFetch: Promise<PortCallTypeRef[]> | undefined
let isFetched = false

function newId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  return `local-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

async function fetch(params?: {
  forceFetch?: boolean
}): Promise<PortCallTypeRef[]> {
  if (isFetched && !params?.forceFetch) {
    return portCallTypes.value
  }
  if (inFlightFetch && !params?.forceFetch) {
    return inFlightFetch
  }
  isLoading.portCallTypes = true
  inFlightFetch = (async () => {
    try {
      const result = await getPortCallTypesRequest()
      portCallTypes.value = sortPortCallTypesByName(result)
      isFetched = true
      return portCallTypes.value
    } finally {
      inFlightFetch = undefined
      isLoading.portCallTypes = false
    }
  })()
  return inFlightFetch
}

// The add/update/remove mutations manipulate local state only — there are no
// write endpoints yet. Wire them to the real management endpoints once the
// backend lands; the `PortCallTypesService` contract stays the same, only
// these bodies change.
async function add(payload: AddPortCallTypePayload): Promise<PortCallTypeRef> {
  const created: PortCallTypeRef = { id: newId(), name: payload.name }
  portCallTypes.value = upsertPortCallType(portCallTypes.value, created)
  return created
}

async function update(
  id: string,
  payload: UpdatePortCallTypePayload
): Promise<PortCallTypeRef> {
  const existing = portCallTypes.value.find((t) => t.id === id)
  if (!existing) throw new Error(`PortCallType ${id} not found`)
  const updated: PortCallTypeRef = {
    id: existing.id,
    name: payload.name ?? existing.name
  }
  portCallTypes.value = upsertPortCallType(portCallTypes.value, updated)
  return updated
}

async function remove(id: string): Promise<void> {
  portCallTypes.value = removePortCallTypeById(portCallTypes.value, id)
}

/**
 * Reactive datasource for the port-call-types registry.
 *
 * Mirrors the `PortCallTypesService` contract from
 * `@gat-ship/types/injection-keys`, so the value returned here can be
 * `provide()`d under `portCallTypesKey` and consumed anywhere in the tree
 * without the consumer importing this package. The underlying state is a
 * module singleton — multiple providers hand out the same registry, and
 * mutations propagate to all of them.
 */
export function usePortCallTypesDataSource(): PortCallTypesService {
  return {
    portCallTypes,
    isLoading,
    fetch,
    add,
    update,
    remove
  }
}
